import { booleanFormulaOperand, dateFormulaOperand, formulaOperands, listFormulaOperand, parseFormula, printFormula, type FormulaAst } from '@/uptk/core/components/dashboard/data/formula';
import type { ModuleSpecField } from './specTypes';

export function renameOperand(formula:string, previous:string, next:string):string {
  const rename=(ast:FormulaAst):FormulaAst=>{
    if(ast.kind==='ref')return {...ast,name:ast.name===previous?next:ast.name};
    if(ast.kind==='neg')return {...ast,value:rename(ast.value)};
    if(ast.kind==='bin')return {...ast,left:rename(ast.left),right:rename(ast.right)};
    if(ast.kind==='call')return {...ast,args:ast.args.map(rename)};
    return ast;
  };
  return printFormula(rename(parseFormula(formula,'row')));
}

export function computedOrder(fields:ModuleSpecField[]):string[] {
  const byName=new Map(fields.map(f=>[f.name,f])),order:string[]=[],path=new Set<string>();
  const visit=(name:string,depth:number,booleanOperand=false,listOperand=false,dateOperand=false):void=>{
    const field=byName.get(name);if(!field)throw new Error('field.computed_unknown_operand');
    // QUIZ-NATIVE2 GAP-Q10 : pick() lit une liste fermée (select statique) ou un texte, jamais un calcul ; FIN-PARITY3 : months_until lit une date.
    if(dateOperand ? !['date','datetime'].includes(field.type) : listOperand ? !['select','text'].includes(field.type)||!!field.computed : booleanOperand ? field.type!=='checkbox' : !['number','amount'].includes(field.type))throw new Error('field.computed_operand_type');
    if(field.protection?.tokenize)throw new Error('field.computed_operand_protected');
    if(field.computed&&field.protection?.personalData&&!field.computed.serverOnly)throw new Error('field.computed_operand_protected');
    if(!field.computed)return;
    if(depth>3)throw new Error('field.computed_chain');
    if(path.has(name))throw new Error('field.computed_cycle');
    path.add(name);
    const ast=parseFormula(field.computed.formula,'row');
    for(const ref of formulaOperands(ast)){const operand=byName.get(ref);if(operand&&!permitsComputedOperand(field,operand))throw new Error('field.computed_operand_protected');visit(ref,depth+1,booleanFormulaOperand(ast,ref),listFormulaOperand(ast,ref),dateFormulaOperand(ast,ref));}
    path.delete(name);if(!order.includes(name))order.push(name);
  };
  if(fields.filter(f=>f.computed).length>20)throw new Error('field.computed_bounds');
  for(const field of fields)if(field.computed)visit(field.name,1);
  return order;
}
export function permitsComputedOperand(target:ModuleSpecField,operand:ModuleSpecField):boolean{
 if(operand.protection?.tokenize)return false;
 if(!operand.protection?.personalData)return true;
 const own=target.protection?.classification??'',source=operand.protection?.classification??'';
 return target.computed?.serverOnly===true&&['amount','number'].includes(operand.type)&&target.protection?.personalData===true&&['C3','C4'].includes(own)&&['C3','C4'].includes(source)&&own>=source;
}

export function computedDependents(fields:ModuleSpecField[],name:string):string[] {
  const affected=new Set([name]);
  for(let pass=0;pass<fields.length;pass++) for(const field of fields) {
    try { if(field.computed && formulaOperands(parseFormula(field.computed.formula,'row')).some(ref=>affected.has(ref))) affected.add(field.name); }
    catch { /* Invalid formulas remain visible and are never rewritten by text replacement. */ }
  }
  return fields.filter(field=>field.name!==name && affected.has(field.name)).map(field=>field.name);
}

export function computedLevel(fields: ModuleSpecField[], field: ModuleSpecField, visiting = new Set<string>()): number {
  const own = Number(String(field.protection?.classification ?? 'C2').slice(1));
  if (!field.computed || visiting.has(field.name)) return own;
  try {
    return Math.max(own, ...formulaOperands(parseFormula(field.computed.formula, 'row')).map(name => {
      const operand = fields.find(item => item.name === name);
      return operand ? computedLevel(fields, operand, new Set([...visiting, field.name])) : 2;
    }));
  } catch { return own; }
}
